
import { useRef, useEffect, useState } from 'react';
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useLanguage } from "@/contexts/LanguageContext";
import { PaymentMethodType } from "../../types/payment";
import { BANK_IBAN } from "../../utils/constants";
import { initializePaymentContainer } from "@/utils/paymentSanitizer";
import { devLog } from "@/utils/secureLogger";

// Add TypeScript declaration for Moyasar
declare global {
  interface Window {
    Moyasar: {
      init: (config: any) => void;
    }
  }
}

interface PaymentFormProps {
  amount: number;
  paymentMethod: PaymentMethodType;
  onPaymentMethodChange: (value: PaymentMethodType) => void;
  onBankTransferReadyChange?: (ready: boolean) => void;
}

const MOYASAR_PUBLISHABLE_KEY = import.meta.env.VITE_MOYASAR_PUBLISHABLE_KEY || '';

const PaymentForm = ({ amount, paymentMethod, onPaymentMethodChange, onBankTransferReadyChange }: PaymentFormProps) => {
  const { t, language } = useLanguage();
  const paymentFormRef = useRef<HTMLDivElement>(null);
  const [bankTransferUnderstood, setBankTransferUnderstood] = useState(false);

  // Notify parent when bank transfer ready state changes
  useEffect(() => {
    if (onBankTransferReadyChange) {
      // Ready if not bank transfer, or if bank transfer and checkbox is checked
      const isReady = paymentMethod !== 'bank_transfer' || bankTransferUnderstood;
      onBankTransferReadyChange(isReady);
    }
  }, [bankTransferUnderstood, paymentMethod, onBankTransferReadyChange]);

  const formatNumber = (num: number) => {
    return num.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const formatLabel = (label: string) => {
    return language === 'ar' ? `: ${label}` : `${label}:`;
  };

  useEffect(() => {
    if (paymentFormRef.current && paymentMethod === 'creditcard' && window.Moyasar) {
      // Safely initialize container with sanitization
      const initialized = initializePaymentContainer(paymentFormRef.current);
      
      if (!initialized) {
        devLog('Failed to initialize payment container');
        return;
      }

      // Get the absolute URL for the callback
      const callbackUrl = `${window.location.origin}/campaign/payment/callback`;
      devLog("Setting Moyasar callback URL to:", callbackUrl);

      window.Moyasar.init({
        element: '.mysr-form',
        amount: Math.round(amount * 100),
        currency: 'SAR',
        description: 'DOOH Campaign Payment',
        publishable_api_key: MOYASAR_PUBLISHABLE_KEY,
        callback_url: callbackUrl,
        methods: ['creditcard'],
      });
    }
  }, [amount, paymentMethod]);

  return (
    <div className={language === 'ar' ? 'text-right' : ''}>
      <RadioGroup
        defaultValue="bank_transfer"
        value={paymentMethod}
        onValueChange={(value) => onPaymentMethodChange(value as PaymentMethodType)}
        className="flex flex-col space-y-3 mb-6"
      >
        <div className={`flex items-center gap-3 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
          <RadioGroupItem value="bank_transfer" id="bank_transfer" />
          <Label htmlFor="bank_transfer" className="font-bold">{t('bankTransfer')}</Label>
        </div>
        <div className={`flex items-center gap-3 opacity-50 cursor-not-allowed ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
          <RadioGroupItem value="creditcard" id="creditcard" disabled />
          <Label htmlFor="creditcard" className="font-bold text-muted-foreground">{t('creditCard')}</Label>
          <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
            {language === 'ar' ? 'قريباً' : 'Coming soon'}
          </span>
        </div>
      </RadioGroup>

      {paymentMethod === 'bank_transfer' && (
        <div className="mt-4 p-4 border border-border rounded-md bg-muted/50 dark:bg-muted/20">
          <div className={`flex justify-between items-center mb-4 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
            <span className="text-base font-medium text-muted-foreground">{formatLabel(t('totalAmount'))}</span>
            <span className="text-lg font-bold text-primary">{formatNumber(amount)} SAR</span>
          </div>
          <p className="text-base md:text-lg font-medium text-muted-foreground leading-relaxed">
            {t('bankTransferInstructions')}
          </p>
          <div className="mt-3 space-y-2">
            <div className={`flex justify-between ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
              <span className="font-medium">{formatLabel(t('iban'))}</span>
              <span className="font-mono">{BANK_IBAN}</span>
            </div>
            <div className={`flex justify-between ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
              <span className="font-medium">{formatLabel(t('bankName'))}</span>
              <span>Alrajhi Bank</span>
            </div>
            <div className={`flex justify-between ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
              <span className="font-medium">{formatLabel(t('emailAddress'))}</span>
              <span>Sales@Fusion-Media.sa</span>
            </div>
          </div>
          
          {/* I have read and understand checkbox */}
          <div className="flex items-start gap-3 mt-4 p-3 border border-border rounded-lg bg-card dark:bg-card">
            <Checkbox
              id="bank-transfer-understood"
              checked={bankTransferUnderstood}
              onCheckedChange={(checked) => setBankTransferUnderstood(checked === true)}
              className="mt-0.5"
            />
            <Label htmlFor="bank-transfer-understood" className="text-sm text-muted-foreground cursor-pointer leading-relaxed">
              {language === 'ar' 
                ? 'لقد قرأت وفهمت أن حملتي ستبدأ فقط بعد تأكيد استلام الدفع.'
                : 'I have read and understand that my campaign will only start after the payment is confirmed as received.'
              }
            </Label>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentForm;
